import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"; 
import { MultiSelect, OptionType } from "@/components/ui/multi-select";
import { useQuery } from "@tanstack/react-query";
import { Player, Shortlist } from "@/lib/types";
import { countries, playingLevels, mainPositions, specificPositions, eventOptions } from "@/lib/filter-data";

interface PlayerFiltersProps {
  onFilterChange: (filters: Record<string, string>) => void;
  onRefreshClick?: () => void;
  isRefreshLoading?: boolean;
  filteredPlayerCount?: number;
}

export default function PlayerFilters({ 
  onFilterChange, 
  onRefreshClick,
  isRefreshLoading,
  filteredPlayerCount
}: PlayerFiltersProps) {
  // Basic filters
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("basic");
  
  // Multi-select filters
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedSpecificPositions, setSelectedSpecificPositions] = useState<string[]>([]);
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  
  // Advanced filters
  const [ageRange, setAgeRange] = useState<[number, number]>([15, 22]);
  const [heightRange, setHeightRange] = useState<[number, number]>([150, 210]); // cm
  const [weightRange, setWeightRange] = useState<[number, number]>([50, 100]); // kg
  const [gpaRange, setGpaRange] = useState<[number, number]>([1.0, 5.0]);
  const [hsGradYearRange, setHsGradYearRange] = useState<[number, number]>([2024, 2028]); 
  const [internationalCaps, setInternationalCaps] = useState("any"); // "any", "yes", "no"
  
  // Track which filters have been actively set by user interaction
  const [activeFilterTracking, setActiveFilterTracking] = useState({
    age: false,
    height: false,
    weight: false,
    gpa: false,
    hsGradYear: false
  });
  
  // Unit toggles
  const [useImperialHeight, setUseImperialHeight] = useState(true); // true = feet/inches, false = cm
  const [useImperialWeight, setUseImperialWeight] = useState(true); // true = lbs, false = kg
  
  // Convert main positions array to options format
  const positionOptions: OptionType[] = [
    { label: "All", value: "all" },
    ...mainPositions.map(pos => ({ label: pos, value: pos }))
  ];
  
  // Convert specific positions array to options format
  const specificPositionOptions: OptionType[] = [
    { label: "All", value: "all" },
    ...specificPositions.map(pos => ({ label: pos, value: pos }))
  ];
  
  // Convert countries array to options format
  const nationalityOptions: OptionType[] = [
    { label: "All Nationalities", value: "all" },
    ...countries.map(country => ({ label: country, value: country }))
  ];
  
  // Check if we need to use mock data (can be set globally or per-component as needed)
  const useMockData = import.meta.env?.DEV || false;
  
  // Import mock data if we're in development
  const { data: mockData } = useQuery({
    queryKey: ["/mock-data"],
    queryFn: async () => {
      // Dynamically import mock data in development
      const { mockPlayers, mockShortlists } = await import("@/lib/mock-data");
      return { players: mockPlayers as Partial<Player>[], shortlists: mockShortlists as Partial<Shortlist>[] };
    },
    enabled: useMockData
  });
  
  // Get players to generate filter options
  const { data: apiPlayers = [], isLoading: isLoadingPlayers, error: playersError } = useQuery<Player[]>({
    queryKey: ["/api/players/"],
    enabled: true, // Always enabled to get real player data
  });
  
  // Use API data for filter generation, fallback to mock data only if API data is empty or loading
  const players = ((apiPlayers as Player[]).length > 0 ? (apiPlayers as Player[]) : (mockData?.players || [])) as Player[];
  
  // Convert playing levels array to options format - dynamically includes levels from player database
  const levelOptions: OptionType[] = useMemo(() => {
    // Extract unique playing levels from the coach's player database
    const uniquePlayingLevelsFromDatabase = Array.from(
      new Set(
        players
          .map(player => player.playing_level)
          .filter((level): level is string => level != null && level.trim() !== "") // Type guard to ensure string
      )
    );
    
    // Combine predefined playing levels with unique levels from database
    const allPlayingLevels = Array.from(
      new Set([...playingLevels, ...uniquePlayingLevelsFromDatabase])
    ).sort(); // Sort alphabetically for consistent ordering
    
    return [
      { label: "All Levels", value: "all" },
      ...allPlayingLevels.map(level => ({ label: level, value: level }))
    ];
  }, [players]);
  
  // Convert event options array to options format - dynamically includes events from player database
  const eventOptionsList: OptionType[] = useMemo(() => {
    // Extract unique events from the coach's player database (flatten arrays)
    const uniqueEventsFromDatabase = Array.from(
      new Set(
        players
          .flatMap(player => Array.isArray(player.event) ? player.event : [])
          .filter((event): event is string => event != null && event.trim() !== "")
      )
    );
    // Combine predefined event options with unique events from database
    const allEventOptions = Array.from(
      new Set([...eventOptions, ...uniqueEventsFromDatabase])
    ).sort();
    return allEventOptions.map(option => ({ label: option, value: option }));
  }, [players]);
  
  // Update filters when inputs change
  useEffect(() => {
    const filters: Record<string, string | string[]> = {};
    
    // Basic filters
    if (searchTerm) filters.name = searchTerm;
    
    // Multi-select filters - handle selected items
    if (selectedPositions.length > 0) {
      if (selectedPositions.includes("all")) {
        // If "All" is selected, don't apply position filter
      } else {
        // Single position selected - use the position field for filtering
        if (selectedPositions.length === 1) {
          filters.position = selectedPositions[0];
        } else {
          // Multiple positions - will be handled server-side
          filters.positions = selectedPositions;
        }
      }
    }
    
    if (selectedNationalities.length > 0) {
      if (selectedNationalities.includes("all")) {
        // If "All" is selected, don't apply nationality filter
      } else {
        filters.nationalities = selectedNationalities;
      }
    }
    
    if (selectedLevels.length > 0) {
      if (selectedLevels.includes("all")) {
        // If "All" is selected, don't apply level filter
      } else {
        filters.playingLevels = selectedLevels;
      }
    }

    if (selectedEvents.length > 0) {
      if (selectedEvents.includes("all")) {
        // If "All" is selected, don't apply event filter
      } else {
        filters.events = selectedEvents;
      }
    }
    
    // Advanced filters - only add if user has actively set them
    if (activeFilterTracking.age) {
      filters.ageMin = ageRange[0].toString();
      filters.ageMax = ageRange[1].toString();
    }
    
    if (activeFilterTracking.height) {
      // Handle unit conversions
      if (useImperialHeight) {
        // Convert from cm to inches for filtering
        filters.heightMin = Math.round(heightRange[0] / 2.54).toString();
        filters.heightMax = Math.round(heightRange[1] / 2.54).toString();
      } else {
        filters.heightMin = heightRange[0].toString();
        filters.heightMax = heightRange[1].toString();
      }
      
      // Include unit information only if height filter is active
      filters.heightUnit = useImperialHeight ? "imperial" : "metric";
    }
    
    if (activeFilterTracking.weight) {
      if (useImperialWeight) {
        // Convert from kg to lbs for filtering
        filters.weightMin = Math.round(weightRange[0] * 2.20462).toString();
        filters.weightMax = Math.round(weightRange[1] * 2.20462).toString();
      } else {
        filters.weightMin = weightRange[0].toString();
        filters.weightMax = weightRange[1].toString();
      }
      
      // Include unit information only if weight filter is active
      filters.weightUnit = useImperialWeight ? "imperial" : "metric";
    }
    
    if (activeFilterTracking.gpa) {
      filters.gpaMin = gpaRange[0].toString();
      filters.gpaMax = gpaRange[1].toString();
    }
    
    if (activeFilterTracking.hsGradYear) {
      filters.hsGradYearMin = hsGradYearRange[0].toString();
      filters.hsGradYearMax = hsGradYearRange[1].toString();
    }
    
    if (internationalCaps !== "any") {
      filters.internationalCaps = internationalCaps;
    }
    
    // Specific positions filter
    if (selectedSpecificPositions.length > 0) {
      if (selectedSpecificPositions.includes("all")) {
        // If "All" is selected, don't apply specific position filter
      } else {
        filters.specificPositions = selectedSpecificPositions;
      }
    }
    
    // Convert filters to a format the parent component expects
    const stringFilters: Record<string, string> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        stringFilters[key] = value.join(',');
      } else {
        stringFilters[key] = value;
      }
    });
    
    onFilterChange(stringFilters);
  }, [
    searchTerm, 
    selectedPositions,
    selectedSpecificPositions,
    selectedNationalities,
    selectedLevels,
    selectedEvents,
    ageRange, 
    heightRange,
    weightRange,
    gpaRange,
    hsGradYearRange,
    internationalCaps,
    useImperialHeight,
    useImperialWeight,
    activeFilterTracking,
    onFilterChange
  ]);
  
  // Unit conversion utilities
  const cmToFeetInches = (cm: number): string => {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}'${inches}"`;
  };
  
  // Display units based on user preference
  const displayHeight = (range: [number, number]): string => {
    if (useImperialHeight) {
      return `${cmToFeetInches(range[0])} - ${cmToFeetInches(range[1])}`;
    } else {
      return `${range[0]} - ${range[1]} cm`;
    }
  };
  
  const displayWeight = (range: [number, number]): string => {
    if (useImperialWeight) {
      return `${Math.round(weightRange[0] * 2.20462)} - ${Math.round(weightRange[1] * 2.20462)} lbs`;
    } else {
      return `${range[0]} - ${range[1]} kg`;
    }
  };
  
  // Toggle height units
  const toggleHeightUnits = () => {
    setUseImperialHeight(!useImperialHeight);
  };
  
  // Toggle weight units
  const toggleWeightUnits = () => {
    setUseImperialWeight(!useImperialWeight);
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    // Basic filters
    setSearchTerm("");
    setSelectedPositions([]);
    setSelectedSpecificPositions([]);
    setSelectedNationalities([]);
    setSelectedLevels([]);
    setSelectedEvents([]);
    
    // Advanced filters
    setAgeRange([15, 22]);
    setHeightRange([150, 210]);
    setWeightRange([50, 100]);
    setGpaRange([1.0, 5.0]);
    setHsGradYearRange([2024, 2028]);
    setInternationalCaps("any");
    
    // Reset active filter tracking
    setActiveFilterTracking({
      age: false,
      height: false,
      weight: false,
      gpa: false,
      hsGradYear: false
    });
    
    // Reset to basic tab
    setActiveTab("basic");
    
    // Notify parent
    onFilterChange({});
  };
  

  
  // Handle refresh emails
  const handleRefresh = () => {
    if (onRefreshClick) {
      onRefreshClick();
    }
  };
  
  // Calculate applied filters for display
  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (searchTerm) {
      activeFilters.push({ 
        id: "search", 
        label: "Search", 
        value: searchTerm,
        removeFilter: () => setSearchTerm("")
      });
    }
    
    if (selectedPositions.length > 0 && !selectedPositions.includes("all")) {
      activeFilters.push({ 
        id: "position", 
        label: "Position", 
        value: selectedPositions.join(", "),
        removeFilter: () => setSelectedPositions([])
      });
    }
    
    if (selectedNationalities.length > 0 && !selectedNationalities.includes("all")) {
      activeFilters.push({ 
        id: "nationality", 
        label: "Nationality", 
        value: selectedNationalities.join(", "),
        removeFilter: () => setSelectedNationalities([])
      });
    }
    
    if (selectedLevels.length > 0 && !selectedLevels.includes("all")) {
      activeFilters.push({ 
        id: "level", 
        label: "Level", 
        value: selectedLevels.join(", "),
        removeFilter: () => setSelectedLevels([])
      });
    }

    if (selectedEvents.length > 0 && !selectedEvents.includes("all")) {
      activeFilters.push({
        id: "event",
        label: "Event",
        value: selectedEvents.join(", "),
        removeFilter: () => setSelectedEvents([])
      });
    }
    
    if (activeFilterTracking.age) {
      activeFilters.push({ 
        id: "age", 
        label: "Age", 
        value: `${ageRange[0]} - ${ageRange[1]} years`,
        removeFilter: () => {
          setAgeRange([15, 22]);
          setActiveFilterTracking(prev => ({ ...prev, age: false }));
        }
      });
    }
    
    if (activeFilterTracking.height) {
      activeFilters.push({ 
        id: "height", 
        label: "Height", 
        value: displayHeight(heightRange),
        removeFilter: () => {
          setHeightRange([150, 210]);
          setActiveFilterTracking(prev => ({ ...prev, height: false }));
        }
      });
    }
    
    if (activeFilterTracking.weight) {
      activeFilters.push({ 
        id: "weight", 
        label: "Weight", 
        value: displayWeight(weightRange),
        removeFilter: () => {
          setWeightRange([50, 100]);
          setActiveFilterTracking(prev => ({ ...prev, weight: false }));
        }
      });
    }
    
    if (activeFilterTracking.gpa) {
      activeFilters.push({ 
        id: "gpa", 
        label: "GPA", 
        value: `${gpaRange[0]} - ${gpaRange[1]}`,
        removeFilter: () => {
          setGpaRange([1.0, 5.0]);
          setActiveFilterTracking(prev => ({ ...prev, gpa: false }));
        }
      });
    }
    
    if (activeFilterTracking.hsGradYear) {
      activeFilters.push({ 
        id: "hsGradYear", 
        label: "HS Grad Year", 
        value: `${hsGradYearRange[0]} - ${hsGradYearRange[1]}`,
        removeFilter: () => {
          setHsGradYearRange([2024, 2028]);
          setActiveFilterTracking(prev => ({ ...prev, hsGradYear: false }));
        }
      });
    }
    
    if (internationalCaps !== "any") {
      activeFilters.push({ 
        id: "international", 
        label: "Int'l Experience", 
        value: internationalCaps === "yes" ? "Has international caps" : "No international caps",
        removeFilter: () => setInternationalCaps("any")
      });
    }
    
    if (selectedSpecificPositions.length > 0 && !selectedSpecificPositions.includes("all")) {
      activeFilters.push({ 
        id: "specificPosition", 
        label: "Specific Position", 
        value: selectedSpecificPositions.join(", "),
        removeFilter: () => setSelectedSpecificPositions([])
      });
    }
    
    return activeFilters;
  };
  
  const activeFilters = getActiveFilters();
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-neutral-200 mb-6 w-full max-w-full overflow-hidden">
      {/* Search and Filter Control Bar */}
      <div className="flex flex-col md:flex-row md:items-end space-y-4 md:space-y-0 md:space-x-4 mb-6">
        <div className="flex-1 space-y-4 min-w-0">
          <div>
            <label htmlFor="search-players" className="text-sm font-medium text-neutral-700 mb-1 block">
              Search Players
            </label>
            <div className="relative">
              <Input
                id="search-players"
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-neutral-400"></i>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetFilters}
            className="text-sm"
          >
            <i className="fas fa-undo mr-1.5"></i>
            Reset Filters
          </Button>
          
          {onRefreshClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshLoading}
              className="text-sm"
            >
              <i className="fas fa-sync-alt mr-1.5"></i>
              {isRefreshLoading ? "Refreshing..." : "Refresh Emails"}
            </Button>
          )}
        </div>
      </div>
      
      {/* Active Filters Display */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 p-3 bg-primary/5 rounded-md border border-primary/20 w-full max-w-full overflow-hidden">
          <div className="text-sm font-semibold text-primary-foreground mr-2 py-1 px-2 bg-primary rounded-lg flex-shrink-0">Active Filters</div>
          {activeFilters.map((filter) => (
            <div 
              key={filter.id} 
              className="flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium flex-shrink-0"
            >
              <span className="font-bold mr-1">{filter.label}:</span> 
              <span className="truncate max-w-32">{filter.value}</span>
              <button 
                onClick={filter.removeFilter}
                className="ml-2 text-primary hover:text-primary/80 focus:outline-none flex-shrink-0"
                aria-label={`Remove ${filter.label} filter`}
              >
                <i className="fas fa-times-circle"></i>
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Tabs-based Filter Categories */}
      <div className="border rounded-lg w-full max-w-full overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-2 grid-rows-2 sm:grid-cols-4 sm:grid-rows-1 gap-1 p-1 bg-neutral-100 rounded-t-lg h-auto">
            <TabsTrigger 
              value="basic" 
              className="rounded py-2 px-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Basic
            </TabsTrigger>
            <TabsTrigger 
              value="physical" 
              className="rounded py-2 px-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Physical
            </TabsTrigger>
            <TabsTrigger 
              value="academic" 
              className="rounded py-2 px-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Academic
            </TabsTrigger>
            <TabsTrigger 
              value="experience" 
              className="rounded py-2 px-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Experience
            </TabsTrigger>
          </TabsList>
          
          <div className="p-5 w-full max-w-full overflow-hidden">
            <TabsContent value="basic" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Position filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Position</label>
                  <MultiSelect
                    options={positionOptions}
                    selected={selectedPositions}
                    onChange={setSelectedPositions}
                    placeholder="Select positions"
                    className="w-full"
                    maxDisplayValues={2}
                  />
                </div>
                
                {/* Nationality filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Nationality</label>
                  <MultiSelect
                    options={nationalityOptions}
                    selected={selectedNationalities}
                    onChange={setSelectedNationalities}
                    placeholder="Select nationalities"
                    className="w-full"
                    maxDisplayValues={2}
                    showSelectAll={false}
                  />
                </div>
                
                {/* Age Range filter */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">Age Range</label>
                    <span className="text-sm text-neutral-500 font-medium">{ageRange[0]} - {ageRange[1]} years</span>
                  </div>
                  <Slider
                    defaultValue={[15, 22]}
                    value={ageRange}
                    min={15}
                    max={40}
                    step={1}
                    onValueChange={(value) => {
                      setAgeRange(value as [number, number]);
                      setActiveFilterTracking(prev => ({ ...prev, age: true }));
                    }}
                    className="py-4"
                  />
                </div>
                
                {/* Level filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Playing Level</label>
                  <MultiSelect
                    options={levelOptions}
                    selected={selectedLevels}
                    onChange={setSelectedLevels}
                    placeholder="Select playing levels"
                    className="w-full"
                    maxDisplayValues={2}
                  />
                </div>

                {/* Event filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Event</label>
                  <MultiSelect
                    options={eventOptionsList}
                    selected={selectedEvents}
                    onChange={setSelectedEvents}
                    placeholder="Select events"
                    className="w-full"
                    maxDisplayValues={2}
                  />
                </div>

                {/* Specific Position filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Specific Position</label>
                  <MultiSelect
                    options={specificPositionOptions}
                    selected={selectedSpecificPositions}
                    onChange={setSelectedSpecificPositions}
                    placeholder="Select specific positions"
                    className="w-full"
                    maxDisplayValues={2}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="physical" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Height Range filter */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">Height Range</label>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-neutral-500 font-medium">{displayHeight(heightRange)}</span>
                      <div className="flex items-center space-x-1">
                        <Switch 
                          checked={useImperialHeight} 
                          onCheckedChange={toggleHeightUnits}
                          id="height-unit-toggle"
                        />
                        <Label htmlFor="height-unit-toggle" className="text-xs text-neutral-500">
                          {useImperialHeight ? "ft/in" : "cm"}
                        </Label>
                      </div>
                    </div>
                  </div>
                  <Slider
                    defaultValue={[150, 210]}
                    value={heightRange}
                    min={150}
                    max={220}
                    step={1}
                    onValueChange={(value) => {
                      setHeightRange(value as [number, number]);
                      setActiveFilterTracking(prev => ({ ...prev, height: true }));
                    }}
                    className="py-4"
                  />
                </div>
                
                {/* Weight Range filter */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">Weight Range</label>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-neutral-500 font-medium">{displayWeight(weightRange)}</span>
                      <div className="flex items-center space-x-1">
                        <Switch 
                          checked={useImperialWeight} 
                          onCheckedChange={toggleWeightUnits}
                          id="weight-unit-toggle"
                        />
                        <Label htmlFor="weight-unit-toggle" className="text-xs text-neutral-500">
                          {useImperialWeight ? "lbs" : "kg"}
                        </Label>
                      </div>
                    </div>
                  </div>
                  <Slider
                    defaultValue={[50, 100]}
                    value={weightRange}
                    min={40}
                    max={120}
                    step={1}
                    onValueChange={(value) => {
                      setWeightRange(value as [number, number]);
                      setActiveFilterTracking(prev => ({ ...prev, weight: true }));
                    }}
                    className="py-4"
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="academic" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* GPA Range filter */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">GPA Range</label>
                    <span className="text-sm text-neutral-500 font-medium">{gpaRange[0]} - {gpaRange[1]}</span>
                  </div>
                  <Slider
                    defaultValue={[1.0, 5.0]}
                    value={gpaRange}
                    min={1.0}
                    max={5.0}
                    step={0.1}
                    onValueChange={(value) => {
                      setGpaRange(value as [number, number]);
                      setActiveFilterTracking(prev => ({ ...prev, gpa: true }));
                    }}
                    className="py-4"
                  />
                </div>
                
                {/* HS Graduation Year filter */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">HS Graduation Year</label>
                    <span className="text-sm text-neutral-500 font-medium">{hsGradYearRange[0]} - {hsGradYearRange[1]}</span>
                  </div>
                  <Slider
                    defaultValue={[2024, 2028]}
                    value={hsGradYearRange}
                    min={2023}
                    max={2030}
                    step={1}
                    onValueChange={(value) => {
                      setHsGradYearRange(value as [number, number]);
                      setActiveFilterTracking(prev => ({ ...prev, hsGradYear: true }));
                    }}
                    className="py-4"
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="experience" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* International Caps filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">International Caps</label>
                  <Select value={internationalCaps} onValueChange={setInternationalCaps}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select filter option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="yes">Has International Caps</SelectItem>
                      <SelectItem value="no">No International Caps</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
      

      
      {/* Results Summary */}
      <div className="mt-6 pt-4 border-t border-neutral-200 text-neutral-500 text-sm">
        Found {filteredPlayerCount !== undefined ? filteredPlayerCount : players.length} players
      </div>
    </div>
  );
}